import React, { useState, useRef } from "react";
import { printDocument, PRINT_BLOCKED_MESSAGE } from '../lib/printDocument';
import ServiceInvoice from "./ServiceInvoice";
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Printer, X, Download } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Void document. BIR (RMO 24-2023) requires a void to be a numbered document
 * referencing the invoice it adjusts, kept for 10 years (RR 17-2013). When the
 * sale was already invoiced this prints the fiscal adjustment document; when it
 * was voided off an open ticket it prints a clearly-marked non-fiscal slip.
 */
/**
 * Print CSS shared by every thermal receipt window.
 *
 * Two things here are load-bearing, and both were causes of receipts printing
 * faint enough for staff to complain:
 *
 *  - The body is sized to the PRINTABLE width (72mm on an 80mm roll, 48mm on a
 *    58mm one), never the roll width. Sizing it to the roll overflows the
 *    printable area, so the driver scales the page down to fit and every stroke
 *    thins with it.
 *  - Body text is bold. A thermal head has no grey -- it either fires a dot or
 *    it does not -- so a light stroke prints as scattered dots and reads as
 *    faded. Courier New, the previous face, has the lightest stems of any
 *    common monospace.
 *
 * Tailwind is not available in these windows (document.write builds a blank
 * document), so every rule the receipt relies on lives here or inline.
 */
const PAPER = {
  58: { printable: 48, size: 10 },
  80: { printable: 72, size: 12 },
};

export const receiptPrintCss = (paperMm) => {
  const mm = paperMm === 58 ? 58 : 80;
  const p = PAPER[mm];
  return `
    @page { size: ${mm}mm auto; margin: 0; }
    body {
      font-family: Consolas, 'Lucida Console', 'DejaVu Sans Mono', monospace;
      font-size: ${p.size}px;
      font-weight: 700;
      line-height: 1.25;
      color: #000;
      width: ${p.printable}mm;
      margin: 0 auto;
      padding: 0 1mm;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .header { text-align: center; margin-bottom: 8px; }
    .divider { border-top: 1px solid #000; margin: 7px 0; }
    .item { display: flex; justify-content: space-between; gap: 6px; margin: 4px 0; }
    .footer { text-align: center; margin-top: 12px; }
    .banner { border: 1px solid #000; padding: 5px; margin-bottom: 8px; text-align: center; }
  `;
};

export function VoidSlipPrintDialog({ open, onClose, voidLogId }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!open || !voidLogId) return;
    setLoading(true);
    fetch(`${API_URL}/api/bir/voids/${voidLogId}/slip`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Failed to load void document')))
      .then(setData)
      .catch(() => toast.error('Failed to load void document'))
      .finally(() => setLoading(false));
  }, [open, voidLogId, token]);

  const money = (n) => `₱${Number(n || 0).toFixed(2)}`;

  const bodyText = () => {
    if (!data) return '';
    const L = [];
    const line = (s = '') => L.push(s);
    if (data.is_training_mode) line('*** TRAINING MODE ***');
    line((data.business_name || 'Big Mummy’s').toUpperCase());
    if (data.trade_name) line(data.trade_name);
    if (data.address) line(data.address);
    if (data.tin) line(`TIN: ${data.tin}`);
    if (data.min_number) line(`MIN: ${data.min_number}`);
    if (data.pos_serial_number) line(`SN: ${data.pos_serial_number}`);
    line('--------------------------------');
    line(data.document_title);
    if (data.not_a_receipt_notice) line(data.not_a_receipt_notice);
    line('--------------------------------');
    if (data.document_number) line(`Doc No.   : ${data.document_number}`);
    if (data.void_number != null) line(`VOID #    : ${data.void_number}`);
    if (data.original_receipt_number) line(`Adjusts SI: ${data.original_receipt_number}`);
    line(`Ticket    : ${data.ticket_number}`);
    line(`Bus. Date : ${data.business_date}`);
    line(`Voided at : ${data.voided_at ? new Date(data.voided_at).toLocaleString() : ''}`);
    line('--------------------------------');
    (data.lines || []).forEach(i => line(`${i.quantity} x ${i.name}  ${money(i.amount)}`));
    line('--------------------------------');
    line(`TOTAL VOIDED: ${money(data.total_voided)}`);
    line('');
    line(`Reason: ${data.reason || '-'}`);
    line(`Cashier: ${data.voided_by || '-'}`);
    line(`Approved by: ${data.approved_by || '-'}`);
    line('');
    line(data.retention_notice || '');
    line('');
    line('Signature: ____________________');
    return L.join('\n');
  };

  const handlePrint = () => {
    const ok = printDocument({
      title: 'Void Document',
      css: `@page{size:80mm auto;margin:0}
      body{font-family:Consolas,'Lucida Console','DejaVu Sans Mono',monospace;
           font-size:12px;font-weight:700;color:#000;line-height:1.25;
           width:72mm;margin:0 auto;padding:0 1mm;white-space:pre}`,
      body: bodyText().replace(/</g, '&lt;'),
    });
    if (!ok) toast.error(PRINT_BLOCKED_MESSAGE);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-sm">
        <DialogHeader><DialogTitle>Void Document</DialogTitle></DialogHeader>
        {loading && <div className="py-8 text-center text-muted-foreground">Loading…</div>}
        {data && (
          <>
            {!data.is_fiscal && (
              <p className="text-xs text-amber-500 border border-amber-500/40 rounded p-2">
                Non-fiscal control slip — this sale was never invoiced, so no BIR serial number is issued.
              </p>
            )}
            <pre className="text-xs bg-muted rounded p-3 overflow-x-auto whitespace-pre-wrap" data-testid="void-slip-preview">{bodyText()}</pre>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-border">Close</Button>
          <Button onClick={handlePrint} disabled={!data} className="bg-secondary hover:bg-secondary/90">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReceiptPrintDialog({ open, onClose, ticketId }) {
  const { token } = useAuth();
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();
  
  React.useEffect(() => {
    if (open && ticketId) {
      fetchReceiptData();
    }
  }, [open, ticketId]);
  
  const fetchReceiptData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kds/print/receipt/${ticketId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setReceiptData(data);
    } catch (err) {
      console.error('Failed to load receipt data:', err);
      toast.error('Failed to load receipt');
    }
    setLoading(false);
  };
  
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const ok = printDocument({
      title: `Receipt - ${receiptData?.ticket_number}`,
      css: receiptPrintCss(receiptData?.template?.paper_width_mm),
      body: printContent.innerHTML,
    });
    if (!ok) toast.error(PRINT_BLOCKED_MESSAGE);
  };
  
  if (!receiptData) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-card border-border">
          <div className="text-center py-8">
            {loading ? 'Loading receipt...' : 'No receipt data'}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-secondary" />
            Receipt Preview
          </DialogTitle>
        </DialogHeader>
        
        <div className="bg-white text-black p-4 rounded max-h-[60vh] overflow-y-auto" ref={printRef}>
          <ServiceInvoice data={receiptData} />
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handlePrint} className="bg-secondary hover:bg-secondary/90">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function KOTPrintDialog({ open, onClose, kotId }) {
  const { token } = useAuth();
  const [kotData, setKotData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();
  
  React.useEffect(() => {
    if (open && kotId) {
      fetchKOTData();
    }
  }, [open, kotId]);
  
  const fetchKOTData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kds/print/kot/${kotId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setKotData(data);
    } catch (err) {
      console.error('Failed to load KOT data:', err);
      toast.error('Failed to load KOT');
    }
    setLoading(false);
  };
  
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const ok = printDocument({
      title: `KOT - ${kotData?.kot_number}`,
      css: `
            body { font-family: 'Arial', sans-serif; font-size: 14px; width: 80mm; margin: 0 auto; padding: 10px; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .header h1 { font-size: 18px; margin: 0; }
            .info { display: flex; justify-content: space-between; margin: 10px 0; font-weight: bold; }
            .item { padding: 8px 0; border-bottom: 1px solid #000; }
            .item-qty { font-size: 18px; font-weight: bold; }
            .item-name { font-size: 16px; font-weight: bold; }
            .modifier { font-size: 12px; color: #000; margin-left: 20px; }
            .notes { background: #f0f0f0; padding: 8px; margin-top: 10px; font-style: italic; }
          `,
      body: printContent.innerHTML,
    });
    if (!ok) toast.error(PRINT_BLOCKED_MESSAGE);
  };
  
  if (!kotData) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-card border-border">
          <div className="text-center py-8">
            {loading ? 'Loading KOT...' : 'No KOT data'}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-orange-600" />
            Kitchen Order Ticket
          </DialogTitle>
        </DialogHeader>
        
        <div className="bg-white text-black p-4 rounded max-h-[60vh] overflow-y-auto" ref={printRef}>
          <div className="header">
            <h1>KITCHEN ORDER</h1>
            <div className="text-xl font-bold">{kotData.kot_number}</div>
          </div>
          
          <div className="info">
            <span>Table: {kotData.table_name || 'N/A'}</span>
            <span>Guests: {kotData.guest_count}</span>
          </div>
          
          <div className="info text-sm">
            <span>{kotData.sent_at ? new Date(kotData.sent_at).toLocaleString() : ''}</span>
          </div>
          
          <div style={{ marginTop: '15px' }}>
            {kotData.items?.map((item, idx) => (
              <div key={idx} className="item">
                <span className="item-qty">{item.quantity}x</span>{' '}
                <span className="item-name">{item.name}</span>
                {item.modifiers?.length > 0 && (
                  <div className="modifier">+ {item.modifiers.join(', ')}</div>
                )}
                {item.notes && (
                  <div className="modifier">Note: {item.notes}</div>
                )}
              </div>
            ))}
          </div>
          
          {kotData.notes && (
            <div className="notes">
              <strong>Order Notes:</strong> {kotData.notes}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={handlePrint} className="bg-orange-600 hover:bg-orange-700">
            <Printer className="w-4 h-4 mr-2" /> Print KOT
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SendToKitchenButton({ ticketId, onSent, disabled }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showKOTPrint, setShowKOTPrint] = useState(false);
  const [kotId, setKotId] = useState(null);
  
  const handleSend = async () => {
    if (!ticketId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/kds/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ticket_id: ticketId })
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText || 'Failed to send to kitchen' }));
        throw new Error(err.detail || 'Failed to send to kitchen');
      }
      
      const data = await res.json();
      toast.success(`Sent to kitchen: ${data.kot_number}`);
      setKotId(data.kot_id);
      setShowKOTPrint(true);
      
      if (onSent) onSent();
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };
  
  return (
    <>
      <Button 
        onClick={handleSend} 
        disabled={disabled || loading || !ticketId}
        className="w-full bg-orange-600 hover:bg-orange-700"
        data-testid="send-to-kitchen-btn"
      >
        <Printer className="w-4 h-4 mr-2" />
        {loading ? 'Sending...' : 'Send to Kitchen'}
      </Button>
      
      <KOTPrintDialog
        open={showKOTPrint}
        onClose={() => setShowKOTPrint(false)}
        kotId={kotId}
      />
    </>
  );
}


// ── Provisional Running Bill (NON-FISCAL) ─────────────────────────────
export function RunningBillDialog({ open, onClose, ticketId }) {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef();

  React.useEffect(() => {
    if (open && ticketId) {
      (async () => {
        setLoading(true);
        try {
          const res = await fetch(`${API_URL}/api/kds/print/receipt/${ticketId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!res.ok) throw new Error('Failed to load bill');
          setData(await res.json());
        } catch (err) {
          toast.error('Failed to load running bill');
        }
        setLoading(false);
      })();
    } else if (!open) {
      setData(null);
    }
  }, [open, ticketId, token]);

  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    const ok = printDocument({
      title: `Running Bill - ${data?.ticket_number}`,
      css: receiptPrintCss(data?.template?.paper_width_mm),
      body: printContent.innerHTML,
    });
    if (!ok) toast.error(PRINT_BLOCKED_MESSAGE);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md" data-testid="running-bill-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-secondary" />
            Running Bill (Provisional)
          </DialogTitle>
        </DialogHeader>

        {!data ? (
          <div className="text-center py-8 text-muted-foreground">
            {loading ? 'Loading bill...' : 'No bill data'}
          </div>
        ) : (
          <div className="bg-white text-black p-4 rounded max-h-[60vh] overflow-y-auto" ref={printRef}>
            <div className="header">
              <div className="banner">
                PROVISIONAL BILL<br />THIS IS NOT AN OFFICIAL RECEIPT
              </div>
              <h2 className="font-bold text-lg">{data.outlet_name}</h2>
              <p className="text-xs">{data.outlet_address}</p>
              <p className="text-xs mt-2">{new Date(data.created_at).toLocaleString()}</p>
            </div>

            <div className="divider" />

            <div className="text-sm">
              <div className="item">
                <span>Ticket #:</span>
                <span className="font-bold">{data.ticket_number}</span>
              </div>
              {data.table_name && (
                <div className="item"><span>Table:</span><span>{data.table_name}</span></div>
              )}
              <div className="item"><span>Guests:</span><span>{data.guest_count}</span></div>
            </div>

            <div className="divider" />

            <div className="text-sm">
              {data.items?.map((item, idx) => (
                <div key={idx} className="mb-2">
                  <div className="item">
                    <span>{item.quantity}x {item.name}</span>
                    <span>₱{item.line_total?.toFixed(2)}</span>
                  </div>
                  {item.modifiers?.length > 0 && (
                    <div className="text-xs ml-4 text-gray-600">{item.modifiers.join(', ')}</div>
                  )}
                </div>
              ))}
            </div>

            <div className="divider" />

            <div className="text-sm">
              <div className="item">
                <span>Subtotal</span>
                <span>₱{data.subtotal?.toFixed(2)}</span>
              </div>
              {data.service_charge > 0 && (
                <div className="item">
                  <span>Service Charge</span>
                  <span>₱{data.service_charge?.toFixed(2)}</span>
                </div>
              )}
              {data.vat_amount > 0 && (
                <div className="item">
                  <span>VAT (incl.)</span>
                  <span>₱{data.vat_amount?.toFixed(2)}</span>
                </div>
              )}
              {data.discount_amount > 0 && (
                <div className="item" style={{ color: '#b91c1c' }}>
                  <span>Discount</span>
                  <span>-₱{data.discount_amount?.toFixed(2)}</span>
                </div>
              )}
              <div className="item total mt-2">
                <span>AMOUNT DUE</span>
                <span>₱{data.total?.toFixed(2)}</span>
              </div>
            </div>

            <div className="footer">
              <div>Please proceed to the cashier to settle your bill.</div>
              <div className="mt-2 text-[10px] font-semibold">
                THIS IS NOT AN OFFICIAL RECEIPT
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={handlePrint}
            disabled={!data}
            className="bg-secondary hover:bg-secondary/90"
            data-testid="print-running-bill-confirm-btn"
          >
            <Printer className="w-4 h-4 mr-2" /> Print Bill
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PrintRunningBillButton({ ticketId, disabled }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="outline"
        className="w-full h-12 border-secondary text-secondary hover:bg-secondary/10"
        onClick={() => setOpen(true)}
        disabled={disabled || !ticketId}
        data-testid="print-running-bill-btn"
      >
        <Printer className="w-4 h-4 mr-2" />
        Print Running Bill
      </Button>
      <RunningBillDialog open={open} onClose={() => setOpen(false)} ticketId={ticketId} />
    </>
  );
}
